import { WORKFRONT_LOOKUP_LABELS, WorkfrontLookup, WorkfrontMappingKind } from '@atlas/types'
import { CardShell, SectionCard, SelectField } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useLookupRows } from '@/hooks/useWorkfrontMappings'
import type { LookupListRow } from '@/services/workfront-mappings.service'
import { LookupsTable } from './components/MappingTables'
import { useDrawerContext, useReportLoadFailure } from './tab-support'
import { NEW_ROW_KEY, useFixLink, useMappingEditor } from './useMappingEditor'

/** Every lookup but the PM rules, which have their own tab. */
const EDITABLE_LOOKUPS = Object.values(WorkfrontLookup).filter((lookup) => lookup !== WorkfrontLookup.REGION_TO_PM)

const isLookup = (value: string | null): value is WorkfrontLookup =>
	(EDITABLE_LOOKUPS as string[]).includes(value ?? '')

/**
 * Lookups: the code-to-code tables (cost level → complexity, status → open or
 * closed, template task → stage, team → agency, language → locale), one at a time.
 * `?lookup=…&value=…&edit=1` (an `UNKNOWN_LOOKUP_VALUE`'s Fix) opens that value's row
 * pre-filled; `label` names a value that is an ID, such as a template task.
 */
export function LookupsTab() {
	const [params] = useSearchParams()
	const initial = params.get('lookup')
	const [lookup, setLookup] = useState<WorkfrontLookup>(
		isLookup(initial) ? initial : WorkfrontLookup.COST_LEVEL_TO_COMPLEXITY,
	)
	const query = useLookupRows(lookup)
	const rows = query.data ?? []
	const editor = useMappingEditor(WorkfrontMappingKind.LOOKUP, (key) => rows.find((r) => r.key === key)?.row)
	const context = useDrawerContext()
	useReportLoadFailure(query.isError, 'lookup rows')

	const edit = (row: LookupListRow) =>
		editor.open({
			key: row.key,
			row: row.row,
			title: `Edit ${row.sourceLabel ?? row.row.sourceValue}`,
			sourceLabel: row.sourceLabel,
			canDelete: row.status !== 'deleted',
		})
	useFixLink(query.isSuccess, (fix) => {
		const value = fix.get('value') ?? ''
		const label = fix.get('label')
		const found = rows.find((r) => r.row.sourceValue === value)
		if (found)
			editor.open({ key: found.key, row: found.row, title: `Fix ${found.sourceLabel ?? value}`, fromFix: true })
		else
			editor.open({
				key: NEW_ROW_KEY,
				row: { lookup, sourceValue: value, targetValue: '' },
				title: `Map ${label ?? value}`,
				sourceLabel: label,
				fromFix: true,
			})
	})

	return (
		<div className="space-y-4">
			<SectionCard title="Lookup">
				<div className="flex flex-wrap items-end gap-4">
					<div className="w-72">
						<SelectField
							label="Lookup"
							value={lookup}
							isClearable={false}
							options={EDITABLE_LOOKUPS.map((value) => ({
								value,
								label: WORKFRONT_LOOKUP_LABELS[value],
							}))}
							onChange={(value) => setLookup(value as WorkfrontLookup)}
						/>
					</div>
					<Button
						size="sm"
						onPress={() =>
							editor.open({
								key: NEW_ROW_KEY,
								row: { lookup, sourceValue: '', targetValue: '' },
								title: 'Add a lookup row',
							})
						}
					>
						Add row
					</Button>
				</div>
			</SectionCard>
			<CardShell>
				<LookupsTable
					rows={rows}
					isLoading={query.isLoading}
					isError={query.isError}
					onRetry={() => void query.refetch()}
					onEdit={edit}
				/>
			</CardShell>
			{editor.drawer(context)}
		</div>
	)
}
